import { useState, useEffect, useRef, useCallback } from "react";
import { API } from "../../../api";
import ListContainer from "./ListContainer";
import Loader from "../../Loader";

import "../../../styles/style_admin_carrousel.css";

const defaultMessage = {
	liste: {
		title: "Items de l'élément",
		empty_message: "Aucun item pour le moment",
		create_label: "Créer un item",
		edit_title: "Modifier l'item",
		create_title: "Créer un nouvel item",
	},
	modal_delete: {
		title: "Confirmation de suppression",
		question: "Êtes-vous sûr de vouloir supprimer cet élément ?",
		cancel_label: "Annuler",
		confirm_label: "Supprimer",
	},
};

/**
 * Container générique pour la gestion d'items (CRUD)
 * Gère le cycle de vie complet : liste, création, édition, suppression et prévisualisation
 * 
 * @param {Object} props - Les propriétés du composant
 * @param {string} [props.apiEndpoint="carrousel"] - L'endpoint API pour les opérations CRUD
 * @param {string} [props.pkey="id"] - Le nom de la clé primaire utilisée pour identifier les items
 * @param {Function} props.ListItemComponent - Composant render function pour afficher un item dans la liste
 *        Reçoit: { item, onHover, onEdit, onDelete }
 * @param {Function} props.EditorComponent - Composant render function pour l'édition/création d'un item
 *        Reçoit: { item, mode, onSave, onCancel, onPreviewUpdate, externalImage }
 * @param {Function} props.PreviewComponent - Composant render function pour la prévisualisation
 *        Reçoit: { item, onDropImage, originalAvailable, showOriginal, onToggleOriginal }
 * @param {Object} [props.message={}] - Messages personnalisés pour l'UI (titre, boutons, etc.)
 * @returns {JSX.Element} Le container de gestion
 */
export default function G_Manager_admin_Container_T1({ apiEndpoint = "carrousel", pkey = "id", ListItemComponent, EditorComponent, PreviewComponent, message = {} }) {
	const [Items, setItems] = useState([]);
	const [loading, setLoading] = useState(true);
	const [mode, setMode] = useState("list"); // "list", "edit", "create" \\
	const [currentItem, setCurrentItem] = useState(null);
	const [previewItem, setPreviewItem] = useState(null);
	const [deleteModal, setDeleteModal] = useState(null);
	const [externalImage, setExternalImage] = useState(null);
	const [originalItem, setOriginalItem] = useState(null);
	const [showOriginal, setShowOriginal] = useState(false);
    const dropObjectUrlRef = useRef(null);

	const effectiveMessage = {
		...defaultMessage,
		...message,
		liste: { ...defaultMessage.liste, ...message?.liste, },
		modal_delete: { ...defaultMessage.modal_delete, ...message?.modal_delete, },
	};

	// Charger les Items au montage \\
	useEffect(() => {
		fetchData();
	}, []);

	/**
	 * Récupère les items depuis l'API
	 * Gère les différents formats de réponse (array direct ou objet avec propriété data)
	 */
	const fetchData = async () => {
		setLoading(true);
		try {
			const datas = await API.get(apiEndpoint); // Gérer différents formats de réponse API \\
			const itemsList = Array.isArray(datas) ? datas : (datas.data || []);
			setItems(itemsList);
			if (itemsList.length > 0) setPreviewItem(itemsList[0]);
		} catch (error) {
			console.error(`Erreur chargement ${apiEndpoint} [FB] :`, error);
			setItems([]);
		} finally {
			setLoading(false);
		}
	};

	/**
	 * Gère le survol d'un item dans la liste
	 * Met à jour l'aperçu uniquement en mode liste
	 * @param {Object} item - L'item survolé
	 */
	const handleHoverItem = (item) => {
		if (mode === "list") setPreviewItem(item);
	};

	/**
	 * Passe en mode édition pour un item
	 * Sauvegarde l'item original pour permettre la comparaison
	 * @param {Object} item - L'item à éditer
	 */
	const handleEdit = (item) => {
		setCurrentItem(item);
		setPreviewItem(item);
		setOriginalItem(item);
		setShowOriginal(false);
		setMode("edit");
	};

	/**
	 * Passe en mode création avec un item vide
	 * Initialise les valeurs par défaut
	 */
	const handleCreate = () => {
		setCurrentItem({ imgtitre: "", imgdescription: "", evenement: "aucune", isvisible: true, typesource: "local", imgname: "", extension: "", });
		setPreviewItem({ imgtitre: "", imgdescription: "", evenement: "aucune", typesource: "local", imgname: "", });
		setExternalImage(null);
		setOriginalItem(null);
		setShowOriginal(false);
		setMode("create");
	};

	/**
	 * Affiche la modal de confirmation de suppression
	 * @param {Object} item - L'item à supprimer
	 */
	const handleDeleteRequest = (item) => {
		setDeleteModal(item);
};

	/**
	 * Confirme et exécute la suppression d'un item
	 * Rafraîchit la liste après suppression
	 */
	const handleDeleteConfirm = async () => {
		try {
			await API.delete(apiEndpoint, { [pkey]: deleteModal[pkey] });
			await fetchData();
			setDeleteModal(null);
			if (previewItem?.[pkey] === deleteModal[pkey]) setPreviewItem(Items[0] || null);
		} catch (error) {
			console.error("Erreur suppression:", error);
			alert("Erreur lors de la suppression");
		}
	};

	/**
	 * Sauvegarde un item (création ou édition)
	 * Gère l'upload de fichier si présent
	 * @param {Object|null} formData - Les données du formulaire (null pour annuler)
	 * @param {File|null} [file=null] - Le fichier image à uploader
	 */
	const handleSave = async (formData, file = null) => {
		try {
			if (formData === null) {
				await fetchData();
				setMode("list");
				setCurrentItem(null);
				return;
			}

			const hasFile = !!file;
			if (mode === "create") {
				if (hasFile) await API.post(`${apiEndpoint}/upload`, formData, { image: file });
				else await API.post(apiEndpoint, formData);
			} else {
				if (hasFile) await API.put(`${apiEndpoint}/upload`, { [pkey]: currentItem[pkey] }, formData, { image: file });
				else await API.put(apiEndpoint, { [pkey]: currentItem[pkey] }, formData);
			}

			await fetchData();
			setMode("list");
			setCurrentItem(null);
		} catch (error) {
			console.error("Erreur sauvegarde:", error);
			throw error;
		}
	};

	/**
	 * Annule l'édition/création en cours
	 * Retourne en mode liste et réinitialise les états
	 */
	const handleCancel = () => {
		setMode("list");
		setCurrentItem(null);
		setPreviewItem(Items[0] || null);
		setExternalImage(null);
		setOriginalItem(null);
		setShowOriginal(false);
	};

	/**
	 * Met à jour l'aperçu avec de nouvelles données
	 * Utilisé pour synchroniser l'aperçu avec les modifications du formulaire
	 * @param {Object} updatedData - Les données partielles à fusionner avec l'aperçu actuel
	 */
	const handlePreviewUpdate = useCallback((updatedData) => {
		setPreviewItem((prev) => ({ ...(prev || {}), ...updatedData }));
	}, []);

	/**
	 * Gère le drop d'une image via drag & drop
	 * Crée un Object URL temporaire pour l'aperçu immédiat
	 * @param {File} file - Le fichier image droppé
	 */
	const handleDropImage = (file) => {
		// Nettoyage de l'URL précédente crée via DnD si présente \\
		if (dropObjectUrlRef.current) {
			try { URL.revokeObjectURL(dropObjectUrlRef.current); } catch {}
			dropObjectUrlRef.current = null;
		}

		const ext = file.name.split(".").pop().toLowerCase();
		const url = URL.createObjectURL(file);
		dropObjectUrlRef.current = url;

		setPreviewItem((prev) => ({ ...(prev || {}), imgname: url, typesource: "preview", extension: ext })); // Met à jour l'aperçu immédiatement avec l'Object URL \\

		const payload = { file, url, extension: ext, ts: Date.now() }; // Notifie l'éditeur pour mettre à jour son formulaire \\
		setExternalImage(payload);
	};

	// Nettoyer l'Object URL créé par DnD lors du démontage \\
	useEffect(() => {
		return () => {
			if (dropObjectUrlRef.current) {
				try { URL.revokeObjectURL(dropObjectUrlRef.current); } catch {}
				dropObjectUrlRef.current = null;
			}
		};
	}, []);

	if (loading) return (
		<div className="generic-manager-t1-loading">
			<Loader size="medium" variant="primary" label="Chargement" />
		</div>
	);

    const effectiveItem = showOriginal ? originalItem : previewItem;

	return (
		<div className="generic-manager-t1">
			<div className="generic-manager-t1-container-left">
				{mode === "list" ? (
					<ListContainer
						title={effectiveMessage.liste.title}
						items={Items}
						onCreate={handleCreate}
						emptyMessage={effectiveMessage.liste.empty_message}
						createLabel={effectiveMessage.liste.create_label}
						renderItem={(item) => (
							<ListItemComponent
								key={item[pkey]}
								item={item}
								onHover={handleHoverItem}
								onEdit={handleEdit}
								onDelete={handleDeleteRequest}
							/>
						)}

					/>
				) : (
					<EditorComponent
						item={currentItem}
						mode={mode}
						onSave={handleSave}
						onCancel={handleCancel}
						onPreviewUpdate={handlePreviewUpdate}
						externalImage={externalImage}
					/>
				)}
			</div>

			<div className="generic-manager-t1-container-right">
				<PreviewComponent
					item={effectiveItem}
					onDropImage={mode !== "list" ? handleDropImage : undefined}
					originalAvailable={mode === "edit" && !!originalItem}
					showOriginal={showOriginal}
					onToggleOriginal={() => setShowOriginal((v) => !v)}
				/>

			</div>

			{deleteModal && (
				<div className="delete-modal-overlay" onClick={() => setDeleteModal(null)}>
					<div className="delete-modal" onClick={(e) => e.stopPropagation()}>
						<h3>{effectiveMessage.modal_delete.title}</h3>
						<p>{effectiveMessage.modal_delete.question}</p>
						<p className="modal-delete-item-title"><strong>{deleteModal.imgtitre}</strong></p>
						<div className="modal-actions">
							<button className="btn ghost" onClick={() => setDeleteModal(null)}>{effectiveMessage.modal_delete.cancel_label}</button>
							<button className="btn danger" onClick={handleDeleteConfirm}>{effectiveMessage.modal_delete.confirm_label}</button>
						</div>
					</div>
				</div>
			)}
		</div>
	);
}